/**
 * `/admin/sign-in` — admin SPA sign-in page (post-0027 auth shape).
 *
 * Three-step (or two + forced enrolment) passwordless flow:
 *
 *   1. Email entry → POST /admin/sign-in. Server emails a 6-digit
 *      code; the response is the same generic message whether the
 *      address matches an admin or not (enumeration-safe).
 *   2. Code entry → POST /admin/sign-in/verify-code. Server returns
 *      either `{ challenge: 'totp', mfa_token }` (TOTP enrolled) or
 *      `{ challenge: 'totp_enrolment_required', enrol_token,
 *      otpauth_uri, secret_base32 }` (forced first-login enrolment).
 *   3a. (TOTP path) TOTP code → POST /admin/sign-in/totp. Server
 *       sets the session cookie and returns the admin profile.
 *   3b. (Enrolment path) The SPA shows the QR / base32 secret and a
 *       6-digit input. Submit → POST /admin/totp/enrol-confirm. Server
 *       persists `mfa_secret` and sets the session cookie.
 *
 * On failure copy is deliberately collapsed: the email/code endpoints
 * never tell the visitor "no such admin"; the TOTP endpoints never
 * tell the visitor "wrong code vs. expired vs. wrong admin." The SPA
 * surfaces the same generic copy for the same generic error code.
 */

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { TotpEnrolmentDisplay } from '../../components/TotpEnrolmentDisplay.js';

export interface AdminSignInPageProps {
  client: ApiClient;
  /** Where to send the admin after a successful TOTP / enrol-confirm.
   *  Defaults to `/admin` — tests override to decouple from routing. */
  redirectTo?: string;
}

type Step =
  | { kind: 'enter-email' }
  | { kind: 'enter-code'; email: string }
  | { kind: 'enter-totp'; email: string; mfaToken: string }
  | {
      kind: 'enrol-totp';
      email: string;
      enrolToken: string;
      otpauthUri: string;
      secretBase32: string;
    };

export function AdminSignInPage({
  client,
  redirectTo = '/admin',
}: AdminSignInPageProps): JSX.Element {
  const [step, setStep] = useState<Step>({ kind: 'enter-email' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const navigate = useNavigate();

  const emailInputId = useId();
  const codeInputId = useId();
  const totpInputId = useId();
  const enrolCodeInputId = useId();
  const errorId = useId();

  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const totpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step.kind === 'enter-code') codeInputRef.current?.focus();
    else if (step.kind === 'enter-totp' || step.kind === 'enrol-totp')
      totpInputRef.current?.focus();
    else emailInputRef.current?.focus();
  }, [step.kind]);

  async function handleEmailSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Enter your work email address.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      const result = await client.adminSignIn({ email: trimmedEmail });
      setInfo(result.message);
      setStep({ kind: 'enter-code', email: trimmedEmail });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err
          : "We couldn't request a sign-in code. Please check your connection and try again.",
      );
    } finally {
      setPending(false);
    }
  }

  async function handleCodeSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (step.kind !== 'enter-code') return;
    const trimmedCode = code.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      const result = await client.adminSignInVerifyCode({ email: step.email, code: trimmedCode });
      setCode('');
      if (result.challenge === 'totp') {
        setStep({ kind: 'enter-totp', email: step.email, mfaToken: result.mfa_token });
      } else {
        setStep({
          kind: 'enrol-totp',
          email: step.email,
          enrolToken: result.enrol_token,
          otpauthUri: result.otpauth_uri,
          secretBase32: result.secret_base32,
        });
      }
      setInfo(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_code') {
        setError("That code didn't work. Try again, or request a fresh code.");
      } else if (err instanceof ApiError) {
        setError(err);
      } else {
        setError("We couldn't verify the code. Please check your connection and try again.");
      }
    } finally {
      setPending(false);
    }
  }

  async function handleTotpSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (step.kind !== 'enter-totp') return;
    const trimmedCode = totpCode.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await client.adminSignInTotp({ mfa_token: step.mfaToken, totp_code: trimmedCode });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'mfa_failed') {
        setError(
          "That code didn't work. It may have expired — try again, or sign in from the start.",
        );
      } else if (err instanceof ApiError) {
        setError(err);
      } else {
        setError("We couldn't verify the code. Please check your connection and try again.");
      }
    } finally {
      setPending(false);
    }
  }

  async function handleEnrolConfirmSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (step.kind !== 'enrol-totp') return;
    const trimmedCode = totpCode.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await client.adminTotpEnrolConfirm({
        enrol_token: step.enrolToken,
        totp_code: trimmedCode,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'enrol_failed') {
        setError(
          "That code didn't match the secret you scanned. Try again, or restart the sign-in.",
        );
      } else if (err instanceof ApiError) {
        setError(err);
      } else {
        setError("We couldn't confirm the enrolment. Please check your connection and try again.");
      }
    } finally {
      setPending(false);
    }
  }

  function restart(): void {
    setStep({ kind: 'enter-email' });
    setCode('');
    setTotpCode('');
    setError(null);
    setInfo(null);
  }

  const introCopy: Record<Step['kind'], string> = {
    'enter-email':
      "Sign in with your work email. We'll send you a 6-digit code to confirm it's you.",
    'enter-code': 'Enter the 6-digit code we just emailed to you. It expires in 5 minutes.',
    'enter-totp': 'Enter the 6-digit code shown in your authenticator app right now.',
    'enrol-totp': 'Set up your authenticator app to finish signing in.',
  };

  return (
    <main className="signin-page" aria-labelledby="admin-signin-heading">
      <div className="signin-card">
        <h1 id="admin-signin-heading">Sign in to ISMS Compliance Admin</h1>
        <p className="signin-intro">{introCopy[step.kind]}</p>

        {step.kind === 'enter-email' ? (
          <form onSubmit={handleEmailSubmit} noValidate>
            <label htmlFor={emailInputId}>Email address</label>
            <input
              id={emailInputId}
              ref={emailInputRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={pending}
              aria-describedby={error ? errorId : undefined}
              aria-invalid={error ? true : undefined}
            />
            <button type="submit" disabled={pending}>
              {pending ? 'Sending code…' : 'Send code'}
            </button>
          </form>
        ) : null}

        {step.kind === 'enter-code' ? (
          <form onSubmit={handleCodeSubmit} noValidate>
            <p className="signin-email-echo">
              Code sent to <strong>{step.email}</strong>.
            </p>
            <label htmlFor={codeInputId}>6-digit code from email</label>
            <input
              id={codeInputId}
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              disabled={pending}
              aria-describedby={error ? errorId : undefined}
              aria-invalid={error ? true : undefined}
            />
            <div className="signin-actions">
              <button type="submit" disabled={pending}>
                {pending ? 'Verifying…' : 'Verify code'}
              </button>
              <button
                type="button"
                onClick={restart}
                disabled={pending}
                className="signin-link-button"
              >
                Use a different email
              </button>
            </div>
          </form>
        ) : null}

        {step.kind === 'enter-totp' ? (
          <form onSubmit={handleTotpSubmit} noValidate>
            <p className="signin-email-echo">
              Signing in as <strong>{step.email}</strong>.
            </p>
            <label htmlFor={totpInputId}>
              6-digit verification code from your authenticator app
            </label>
            <input
              id={totpInputId}
              ref={totpInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              required
              disabled={pending}
              aria-describedby={error ? errorId : undefined}
              aria-invalid={error ? true : undefined}
            />
            <div className="signin-actions">
              <button type="submit" disabled={pending}>
                {pending ? 'Verifying…' : 'Verify and sign in'}
              </button>
              <button
                type="button"
                onClick={restart}
                disabled={pending}
                className="signin-link-button"
              >
                Start over
              </button>
            </div>
          </form>
        ) : null}

        {step.kind === 'enrol-totp' ? (
          <section aria-labelledby="enrol-heading">
            <h2 id="enrol-heading">Set up your authenticator app</h2>
            <p>
              You don't have a TOTP secret yet. Scan the QR (or enter the secret manually) into an
              authenticator app, then type a code from the app to confirm.
            </p>
            <TotpEnrolmentDisplay
              otpauthUri={step.otpauthUri}
              secretBase32={step.secretBase32}
              idPrefix="signin-enrol"
            />
            <form onSubmit={handleEnrolConfirmSubmit} noValidate>
              <label htmlFor={enrolCodeInputId}>6-digit code from your authenticator app</label>
              <input
                id={enrolCodeInputId}
                ref={totpInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                required
                disabled={pending}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? true : undefined}
              />
              <div className="signin-actions">
                <button type="submit" disabled={pending}>
                  {pending ? 'Confirming…' : 'Confirm and sign in'}
                </button>
                <button
                  type="button"
                  onClick={restart}
                  disabled={pending}
                  className="signin-link-button"
                >
                  Start over
                </button>
              </div>
            </form>
          </section>
        ) : null}

        {info && step.kind === 'enter-code' ? (
          <p className="signin-info" role="status">
            {info}
          </p>
        ) : null}
        {error ? (
          <div id={errorId}>
            <ErrorBanner error={error} />
          </div>
        ) : null}
      </div>
    </main>
  );
}
